import { memo } from 'react';
import { Loader, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useReverseGeocodeLabel } from '@/hooks/useReverseGeocodeLabel';
import { MAP_SETUP_COORDINATE_DECIMALS } from '@/constants';
import type { Coordinates } from '@/types';
import { ConfigSectionLabel } from './ConfigSectionLabel';
import styles from './MapCenterCard.module.css';

/** Placeholder shown when no map center has been chosen yet. */
const EMPTY_VALUE = '—';
/** Tight gap (px) between the place name and its coordinate caption. */
const PLACE_COORDS_GAP = 2;

interface MapCenterCardProps {
  /** Map center to display, or `null` when none has been chosen yet. */
  center: Coordinates | null;
}

/** Format the center as a single `lat, lng` caption at the configured precision. */
function formatCoords(center: Coordinates): string {
  const decimals = MAP_SETUP_COORDINATE_DECIMALS;
  return `${center.lat.toFixed(decimals)}, ${center.lng.toFixed(decimals)}`;
}

/**
 * Read-only summary for the configurator's map center.
 *
 * Renders a compact, borderless section consistent with the rest of the panel:
 * the reverse-geocoded place name as the primary line, with the raw coordinates
 * (driven by the draggable preview marker) as a dimmed caption below. The place
 * name is informational only and never blocks saving.
 */
export const MapCenterCard = memo(function MapCenterCard({
  center,
}: MapCenterCardProps) {
  const { t } = useTranslation();
  const { label, isLoading } = useReverseGeocodeLabel(center);

  return (
    <Stack gap="xs">
      <ConfigSectionLabel>{t('mapSetup.center.title')}</ConfigSectionLabel>

      {center ? (
        <Stack gap={PLACE_COORDS_GAP}>
          {isLoading ? (
            <Loader size="xs" />
          ) : (
            <Text size="sm" className={styles.placeName}>
              {label}
            </Text>
          )}
          <Text size="xs" c="dimmed" className={styles.coords}>
            {formatCoords(center)}
          </Text>
        </Stack>
      ) : (
        <Text size="sm" c="dimmed">
          {EMPTY_VALUE}
        </Text>
      )}
    </Stack>
  );
});
